import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface PanelProps extends ComponentPropsWithRef<'section'> {
  /** Removes the default padding, for panels that hold a flush table. */
  flush?: boolean;
}

/**
 * The workhorse container: a bordered box with a slight drop shadow, in the
 * style of a classic enterprise "panel". Squared corners and a visible border
 * on every side, rather than a floating card.
 */
export function Panel({ flush = false, className, children, ...props }: PanelProps) {
  return (
    <section
      className={cn(
        'rounded-md border border-border bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.07)]',
        /* A heading bar is meant to be seated in the panel's top edge, so a
           panel that carries one gives up its own padding no matter what the
           call site passed, and `PanelBody` pads the content instead. Written
           as a rule rather than trusted to every call site because forgetting
           `flush` left the bar floating in a 12px white gutter, which is the
           one thing it must never do - and the next panel someone writes would
           have reintroduced it. Panels without a heading, and `flush` ones,
           are untouched: neither matches the selector. */
        !flush && 'p-3 has-[>[data-panel-header]]:p-0',
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export interface PanelHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Actions aligned to the right of the title, typically buttons. */
  actions?: ReactNode;
  className?: string;
}

/** The grey gradient heading bar that sits on top of a panel. */
export function PanelHeader({ title, description, actions, className }: PanelHeaderProps) {
  return (
    <header
      /* The hook `Panel` looks for when it decides whether to pad itself. */
      data-panel-header=""
      className={cn(
        'heading-bar flex items-start justify-between gap-3 rounded-t-md border-b px-3 py-2',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-xs font-bold text-content">{title}</h2>
        {description && <p className="mt-0.5 text-2xs text-content-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </header>
  );
}

export function PanelBody({ className, children, ...props }: ComponentPropsWithRef<'div'>) {
  return (
    <div className={cn('p-3', className)} {...props}>
      {children}
    </div>
  );
}

export function PanelFooter({ className, children, ...props }: ComponentPropsWithRef<'div'>) {
  return (
    <div
      className={cn(
        'chrome-bar flex items-center justify-end gap-1.5 rounded-b-md border-t px-3 py-2',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * The grey filter/search strip that sits under a panel heading. Its own
 * component because every list page needs exactly this bar.
 */
export function PanelToolbar({ className, children, ...props }: ComponentPropsWithRef<'div'>) {
  return (
    <div
      className={cn(
        'chrome-bar flex flex-wrap items-center gap-1.5 border-b px-2 py-1.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
